import { useCallback, useMemo } from "react";
import {
  MenuItem,
  MenuSeparator,
  MenuSub,
  MenuSubTrigger,
  MenuSubPopup,
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
  Icon,
  Text,
} from "@salestrak/ui";

import type { MemberRow, RoleRow } from "#app/lib/data";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { usePermission } from "#app/lib/hooks/use-permission";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MemberRowMenuProps = {
  member: MemberRow;
  availableRoles: RoleRow[];
  onViewProfile: (userId: string) => void;
  onRoleChange: (userId: string, roleId: string) => void;
};

export type ConfirmAction = {
  type: "suspend" | "reactivate" | "cancel-invite";
  member: MemberRow;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Menu items for member row actions. Returns only MenuItem elements.
 * Dialogs are rendered separately via MemberRowMenuDialog.
 */
export function MemberRowMenuItems({
  member,
  availableRoles,
  onViewProfile,
  onRoleChange,
  onRequestSuspend,
  onRequestReactivate,
  onRequestCancelInvite,
}: MemberRowMenuProps & {
  onRequestSuspend: (member: MemberRow) => void;
  onRequestReactivate: (member: MemberRow) => void;
  onRequestCancelInvite: (member: MemberRow) => void;
}) {
  const { resendInvite } = useMemberMutations();

  const canUpdateMembers = usePermission("manage.users");
  const canUpdateRoles = usePermission("manage.users");
  const canResendInvite = usePermission("manage.users");

  const { memberStatus } = member;

  const memberRoleIds = useMemo(
    () => new Set(member.roles.map((r) => r.roleId)),
    [member.roles]
  );

  const handleResendInvite = useCallback(async () => {
    await resendInvite(member.userId);
  }, [member.userId, resendInvite]);

  if (!canUpdateMembers && !canUpdateRoles && !canResendInvite) {
    return null;
  }

  return (
    <>
      <MenuItem onClick={() => onViewProfile(member.userId)}>
        <Icon name="user" size="lg" color="muted" />
        <Text size="sm">View profile</Text>
      </MenuItem>

      {canUpdateRoles && availableRoles.length > 0 ? (
        <>
          <MenuSeparator />
          <MenuSub>
            <MenuSubTrigger>
              <Icon name="shield" size="lg" color="muted" />
              <Text size="sm">Change role</Text>
            </MenuSubTrigger>
            <MenuSubPopup>
              {availableRoles.map((role) => {
                const isSelected = memberRoleIds.has(role.roleId);
                return (
                  <MenuItem
                    key={role.roleId}
                    onClick={() => onRoleChange(member.userId, role.roleId)}
                  >
                    <Text size="sm">{role.roleName}</Text>
                    {isSelected ? (
                      <Icon name="check" size="md" color="primary" />
                    ) : null}
                  </MenuItem>
                );
              })}
            </MenuSubPopup>
          </MenuSub>
        </>
      ) : null}

      {canUpdateMembers && memberStatus === "active" ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={() => onRequestSuspend(member)}>
            <Icon name="x-circle" size="lg" color="destructive" />
            <Text size="sm" variant="destructive">Suspend member</Text>
          </MenuItem>
        </>
      ) : null}

      {canUpdateMembers && memberStatus === "suspended" ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={() => onRequestReactivate(member)}>
            <Icon name="play-circle" size="lg" color="muted" />
            <Text size="sm">Reactivate member</Text>
          </MenuItem>
        </>
      ) : null}

      {memberStatus === "invited" && canResendInvite ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={handleResendInvite}>
            <Icon name="send" size="lg" color="muted" />
            <Text size="sm">Resend invite</Text>
          </MenuItem>
          <MenuItem onClick={() => onRequestCancelInvite(member)}>
            <Icon name="x-circle" size="lg" color="destructive" />
            <Text size="sm" variant="destructive">Cancel invite</Text>
          </MenuItem>
        </>
      ) : null}
    </>
  );
}

/**
 * Confirmation dialog for member suspend/reactivate.
 * Render as a sibling to DataTable, not inside renderRowActions.
 */
export function MemberRowMenuDialog({
  action,
  onClose,
}: {
  action: ConfirmAction | null;
  onClose: () => void;
}) {
  const { updateMember, cancelInvite } = useMemberMutations();

  const memberLabel = action?.member.name ?? action?.member.email ?? "";

  const handleConfirm = useCallback(async () => {
    if (!action) return;
    if (action.type === "cancel-invite") {
      await cancelInvite(action.member.userId);
    } else {
      await updateMember(action.member.userId, {
        memberStatus: action.type === "suspend" ? "suspended" : "active",
      });
    }
    onClose();
  }, [action, updateMember, cancelInvite, onClose]);

  const title =
    action?.type === "suspend"
      ? `Suspend ${memberLabel}?`
      : action?.type === "reactivate"
        ? `Reactivate ${memberLabel}?`
        : `Cancel invite for ${memberLabel}?`;

  const description =
    action?.type === "suspend"
      ? `${memberLabel} will no longer be able to access this workspace.`
      : action?.type === "reactivate"
        ? `${memberLabel} will regain access to this workspace.`
        : `${memberLabel} will be removed from this workspace. The invitation link in their email will stop working.`;

  return (
    <AlertDialog
      open={!!action}
      onOpenChange={(open) => { if (!open) onClose(); }}
    >
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          {action?.type === "reactivate" ? (
            <Button onClick={handleConfirm}>Reactivate member</Button>
          ) : (
            <Button variant="destructive" onClick={handleConfirm}>
              {action?.type === "suspend" ? "Suspend member" : "Cancel invite"}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}